import type { WidgetConfig, WidgetInstance } from "@salestrak/ui";

/**
 * The persisted personal-fork payload: a FULL-board snapshot, not a diff —
 * Reset stays one trivial operation, and a diff-merge invites unresolvable
 * conflicts when the team board changes underneath. Consequence (by design):
 * team updates don't reach a personalized board until Reset. Pure codec so
 * the node test harness can round-trip it; the store owns when it's read
 * and written (`useUserSetting` under `widget-view-personal:<view>`).
 */
export type WidgetViewPersonalSnapshot = {
  version: number;
  widgets: WidgetInstance[];
};

export const PERSONAL_SNAPSHOT_VERSION = 1;

/** Normalizer for the personal-fork setting payload — any malformed or
 *  wrong-version value reads as "no personal layer" (null). */
export function parsePersonalSnapshot(value: unknown): WidgetViewPersonalSnapshot | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as { version?: unknown; widgets?: unknown };
  if (raw.version !== PERSONAL_SNAPSHOT_VERSION || !Array.isArray(raw.widgets)) {
    return null;
  }
  const widgets: WidgetInstance[] = [];
  for (const entry of raw.widgets) {
    if (!entry || typeof entry !== "object") return null;
    const w = entry as Record<string, unknown>;
    if (typeof w.id !== "string" || typeof w.type !== "string") return null;
    if (
      typeof w.x !== "number" ||
      typeof w.y !== "number" ||
      typeof w.w !== "number" ||
      typeof w.h !== "number"
    ) {
      return null;
    }
    widgets.push({
      id: w.id,
      type: w.type,
      config: (w.config ?? null) as WidgetConfig | null,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      schemaVersion: typeof w.schemaVersion === "number" ? w.schemaVersion : 1,
    });
  }
  return { version: PERSONAL_SNAPSHOT_VERSION, widgets };
}
